import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, CONTACT } from "@/lib/api";
import CompanionCard from "@/components/CompanionCard";
import FeaturedSpotlight from "@/components/FeaturedSpotlight";
import { ChevronRight, Diamond, ShieldCheck, Clock, Crown, Key } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/80c4a7d5-1156-4a15-9444-cb1ba4c521cc/images/9cdca5716ba0081cae0c0b6d3d3c8530279220bbb332fdc1587078bab1115f39.png";
const LIFESTYLE_BG =
  "https://static.prod-images.emergentagent.com/jobs/80c4a7d5-1156-4a15-9444-cb1ba4c521cc/images/4d97eee384e223aa9f94a3367f3fe23f0973b1da0f053280eb69bf386ec3bb58.png";
const SILHOUETTE =
  "https://static.prod-images.emergentagent.com/jobs/80c4a7d5-1156-4a15-9444-cb1ba4c521cc/images/c2e444039e8ccb128c1da22fff373edbbd3079f84895039858b69a5e001c137a.png";

export default function Home() {
  const { t } = useI18n();
  const [companions, setCompanions] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/companions").then((r) => setCompanions(r.data || []));
    api.get("/reviews").then((r) => setReviews(r.data || []));
  }, []);

  const featured = companions.filter((c) => c.featured).slice(0, 4);
  const grid = companions.slice(0, 8);

  const values = [
    { icon: Crown, label: t("val_premium"), text: t("val_premium_t") },
    { icon: ShieldCheck, label: t("val_verified"), text: t("val_verified_t") },
    { icon: Diamond, label: t("val_discretion"), text: t("val_discretion_t") },
    { icon: Clock, label: t("val_concierge"), text: t("val_concierge_t") },
  ];

  const pillars = [
    { icon: ShieldCheck, t1: "Discretion", t2: "is Luxury" },
    { icon: Crown, t1: "Exclusive", t2: "Membership" },
    { icon: Key, t1: "Private", t2: "Sessions" },
    { icon: Diamond, t1: "Luxury", t2: "Experience" },
  ];

  return (
    <div data-testid="home-page">
      {/* OFFICIAL BRAND BANNER */}
      <section className="relative pt-28 md:pt-32 pb-20 px-6 md:px-12 bg-black overflow-hidden glamour-glow">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-10">
            <div className="label-pink mb-4">ESCORT · ATHENS · 18+</div>
            <h1 className="font-serif-display text-3xl md:text-5xl font-semibold tracking-[0.18em] uppercase companion-name-luxury gold-glow">
              Bilion Euro Babies
            </h1>
            <div className="monogram-divider mt-4"><span>❖</span></div>
            <div className="label-luxury text-[0.65rem] mt-1 text-white/50">
              Escort Agency · Luxury Beyond Imagination
            </div>
          </div>

          <div className="max-w-3xl mx-auto text-center my-16 fade-up">
            <div className="gold-ornament mb-8"><span>❖</span></div>
            <p className="font-serif-display italic text-2xl md:text-3xl text-white/85 leading-relaxed">
              "Private. Exclusive. <span className="gold-text">Untouchable.</span>"
            </p>
            <div className="gold-ornament mt-8"><span>❖</span></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5 mt-12">
            {pillars.map((p) => (
              <div
                key={p.t1}
                className="bg-[#0a0a0a] p-7 md:p-8 text-center group hover:bg-[#121212] transition-colors duration-700"
              >
                <p.icon
                  size={26}
                  className="text-[var(--gold)] mx-auto mb-4 group-hover:text-[var(--pink)] transition-colors duration-500"
                />
                <div className="label-luxury text-[0.6rem]">{p.t1}</div>
                <div className="font-serif-display text-xl mt-2">{p.t2}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex flex-wrap items-center justify-center gap-3 text-[0.65rem] tracking-[0.4em] uppercase">
              <span className="text-[var(--gold)]">Luxury</span>
              <Diamond size={6} className="text-[var(--pink)]" />
              <span className="text-[var(--pink)]">Pleasure</span>
              <Diamond size={6} className="text-[var(--gold)]" />
              <span className="text-[var(--gold)]">Power</span>
              <Diamond size={6} className="text-[var(--pink)]" />
              <span className="text-[var(--pink)]">Discretion</span>
            </div>
            <div className="label-luxury text-[0.55rem] mt-6 text-white/40">
              By Appointment Only · Private. Exclusive. Untouchable.
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <Link to="/companions" className="btn-gold btn-gold-solid" data-testid="banner-view-companions">
                {t("hero_cta_view")} <ChevronRight size={14} />
              </Link>
              <Link to="/contact" className="btn-gold" data-testid="banner-arrange">
                {t("hero_cta_arrange")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HERO */}
      <section
        className="relative min-h-[100vh] flex items-end overflow-hidden"
        style={{
          background: `linear-gradient(rgba(5,5,5,0.55), rgba(5,5,5,0.85) 70%, #050505), url('${HERO_BG}') center 30%/cover no-repeat`,
        }}
      >
        <div className="grain absolute inset-0" />
        <div className="silk-shimmer" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-40 pb-24 w-full">
          <div className="max-w-4xl">
            <div className="label-luxury mb-8 fade-up">{t("hero_eyebrow")}</div>
            <h1 className="font-serif-display text-[clamp(3rem,9vw,8rem)] leading-[0.95] font-light tracking-tight fade-up delay-200">
              {t("hero_title_1")}{" "}
              <br />
              <span className="italic gold-text">{t("hero_title_2")}</span>
              {" "}<br />
              {t("hero_title_3")}
            </h1>
            <p className="text-white/60 text-base md:text-lg max-w-xl mt-10 leading-relaxed fade-up delay-500">
              {t("hero_text")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-12 fade-up delay-700">
              <Link to="/companions" className="btn-gold btn-gold-solid" data-testid="hero-view-companions">
                {t("hero_cta_view")} <ChevronRight size={14} />
              </Link>
              <Link to="/contact" className="btn-gold" data-testid="hero-arrange">
                {t("hero_cta_arrange")}
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 right-12 hidden md:flex items-center gap-3 text-white/40 text-xs tracking-[0.4em] uppercase">
          <Diamond size={12} className="text-[var(--gold)] gold-pulse" /> {t("hero_badge")}
        </div>
      </section>

      {/* TONIGHT'S SPOTLIGHT — auto-rotating featured slider */}
      <FeaturedSpotlight companions={featured.length > 0 ? featured : companions} />

      {/* VALUES STRIP */}
      <section className="border-y border-white/5 bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-white/5">
          {values.map((v) => (
            <div key={v.label} className="p-8 md:p-10 flex flex-col gap-2">
              <v.icon size={18} className="text-[var(--gold)]" />
              <div className="font-serif-display text-xl mt-1">{v.label}</div>
              <div className="text-white/50 text-xs tracking-wider">
                {v.text}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED VIP */}
      {featured.length > 0 && (
        <section className="py-24 md:py-32 px-6 md:px-12">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
              <div>
                <div className="label-luxury mb-4">{t("inner_circle")}</div>
                <h2 className="font-serif-display text-4xl md:text-6xl font-light tracking-tight">
                  {t("featured_vip")} <span className="italic gold-text">{t("featured_vip_italic")}</span>
                </h2>
              </div>
              <Link
                to="/companions"
                data-testid="see-all-companions"
                className="text-xs tracking-[0.3em] uppercase text-white/60 hover-gold border-b border-white/20 pb-1 self-start md:self-end"
              >
                {t("see_all")}
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {featured.map((c, i) => (
                <CompanionCard key={c.id} c={c} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* EDITORIAL SPLIT */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 bg-[#080808]">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div
            className="aspect-[4/5] bg-cover bg-center grain relative"
            style={{ backgroundImage: `url('${SILHOUETTE}')` }}
          />
          <div>
            <div className="label-luxury mb-6">{t("different_standard")}</div>
            <h2 className="font-serif-display text-4xl md:text-6xl font-light leading-[1.05] tracking-tight">
              {t("we_curate_1")} <span className="italic gold-text">{t("we_curate_everyone")}</span>
              <br /> {t("we_curate_2")} <span className="italic">{t("we_curate_few")}</span>
            </h2>
            <p className="text-white/60 leading-relaxed mt-8 max-w-lg">
              {t("we_curate_text")}
            </p>
            <div className="grid grid-cols-3 mt-12 border-t border-white/10">
              {[
                { n: "100%", l: t("stat_verified") },
                { n: "24/7", l: t("stat_concierge") },
                { n: "0", l: t("stat_compromise") },
              ].map((s) => (
                <div key={s.l} className="py-6 border-r last:border-r-0 border-white/10 px-2">
                  <div className="font-serif-display text-3xl gold-text">
                    {s.n}
                  </div>
                  <div className="label-luxury text-[0.6rem] mt-2 text-white/40">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* THE GALLERY */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <div className="label-luxury mb-4">{t("directory")}</div>
            <h2 className="font-serif-display text-5xl md:text-7xl font-light tracking-tight">
              {t("meet_angels_1")} <span className="italic gold-text">{t("meet_angels_2")}</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {grid.map((c, i) => (
              <CompanionCard key={c.id} c={c} index={i} />
            ))}
          </div>
          <div className="text-center mt-14">
            <Link to="/companions" className="btn-gold" data-testid="home-view-full-directory">
              {t("view_full")}
            </Link>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section
        className="relative py-24 md:py-32 px-6 md:px-12"
        style={{
          background: `linear-gradient(rgba(5,5,5,0.92), rgba(5,5,5,0.96)), url('${LIFESTYLE_BG}') center/cover no-repeat fixed`,
        }}
      >
        <div className="max-w-5xl mx-auto text-center">
          <div className="label-luxury mb-4">{t("whispered")}</div>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light tracking-tight mb-16">
            {t("from_clients_1")} <span className="italic gold-text">{t("from_clients_2")}</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {reviews.slice(0, 3).map((r) => (
              <div
                key={r.id}
                className="border border-white/10 bg-black/40 backdrop-blur-sm p-8 text-left"
                data-testid={`review-${r.id}`}
              >
                <div className="text-[var(--gold)] tracking-[0.3em] mb-4">★ ★ ★ ★ ★</div>
                <p className="text-white/70 text-sm leading-relaxed italic">
                  "{r.text}"
                </p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="font-serif-display text-lg">{r.author}</div>
                  <div className="label-luxury text-[0.6rem] mt-1">
                    {r.source}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Footer Block — invisible to design but readable by Google */}
      <section
        className="sr-only"
        aria-hidden="true"
        data-testid="seo-keywords-block"
      >
        <h2>Billion Euro Babies — VIP Escort Agency in Athens, Greece</h2>
        <p>
          Billion Euro Babies is the premier luxury escort agency in Athens. We offer high-class VIP companions, verified models, and discreet outcall service across Athens, Glyfada, Kifisia, Piraeus, and Kolonaki.
        </p>
        <p>
          Our services include GFE (girlfriend experience), dinner dates, overnight stays, travel companions, hotel visits, βίζιτα στο σπίτι, ραντεβού με βίζιτα, in-call and out-call escort service. Available 24/7 in Athens for international and local clients.
        </p>
        <p>
          Find sexy Colombian, Brazilian, Cuban, Russian, and Greek escort girls in Athens. Real photos, verified profiles, hourly rates from €100 (30 min) to €1,100 (8 hours).
        </p>
        <p>
          Keywords: escort athens, athens escort agency, vip escort athens, luxury escort athens, βίζιτες αθήνα, συνοδοί αθήνα, πουτάνες αθήνα, escort αθήνα, escort sinodoi, vizita athina, vizita sto spiti, kane sex spiti, sex athina, sex athens, girls athens, girls for sex athens, escort service athens, gfe athens, dinner date athens, outcall escort athens, escort glyfada, escort kifisia, escort piraeus, escort kolonaki, latin escort athens, colombian escort athens, hot girls athens, athens night, athens nightlife, hotel visit athens, ξενοδοχείο αθήνα συνοδός, vip συνοδοί αθήνα, vip μοντέλα αθήνα, vip models athens, γαμήσι αθήνα, athina gamisi, in-call out-call athens, billion euro babies, bilion euro babies.
        </p>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 md:px-12 text-center">
        <div className="max-w-3xl mx-auto">
          <Diamond size={20} className="text-[var(--gold)] mx-auto mb-6 gold-pulse" />
          <h2 className="font-serif-display text-5xl md:text-7xl font-light tracking-tight leading-[1.05]">
            {t("cta_tonight_1")} <span className="italic gold-text">{t("cta_tonight_2")}</span>
          </h2>
          <p className="text-white/60 mt-6 max-w-lg mx-auto">{t("cta_text")}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Link to="/contact" className="btn-gold btn-gold-solid" data-testid="cta-arrange">
              {t("cta_arrange")}
            </Link>
            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              className="btn-gold"
              data-testid="cta-whatsapp"
            >
              {t("cta_whatsapp")}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
